import React, { useCallback, useEffect, useRef, useState } from 'react';
import BASE_URL from '../utils/api';

const formatarDataHora = (texto) => {
  const m = String(texto || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : '';
};

// Pop-up com as fotos da entrega da produção (tabela FotosEntregaProducao): adicionar, ver e excluir.
// As imagens vêm do servidor com o login (o banco é o da empresa logada), por isso são buscadas com fetch.
export default function PopupFotosEntregaProducao({ producaoId, onFechar }) {
  const [fotos, setFotos] = useState([]);
  const [urls, setUrls] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const urlsRef = useRef({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const resp = await fetch(`${BASE_URL}/Producoes/${producaoId}/fotos-entrega`);
      const lista = resp.ok ? await resp.json() : [];
      setFotos(Array.isArray(lista) ? lista : []);
      // Miniaturas: busca só as que ainda não foram carregadas
      for (const f of (Array.isArray(lista) ? lista : [])) {
        if (urlsRef.current[f.id]) continue;
        const r = await fetch(`${BASE_URL}/Producoes/fotos-entrega/${f.id}`);
        if (!r.ok) continue;
        urlsRef.current[f.id] = URL.createObjectURL(await r.blob());
        setUrls({ ...urlsRef.current });
      }
    } catch (err) {
      console.error('Erro ao carregar as fotos da entrega:', err);
    } finally {
      setCarregando(false);
    }
  }, [producaoId]);

  useEffect(() => {
    carregar();
    const guardadas = urlsRef.current;
    return () => Object.values(guardadas).forEach(u => URL.revokeObjectURL(u));
  }, [carregar]);

  const enviar = async (arquivos) => {
    const imagens = Array.from(arquivos || []).filter(a => a.type.startsWith('image/'));
    if (imagens.length === 0) { alert('Escolha uma ou mais imagens.'); return; }
    try {
      setEnviando(true);
      const dados = new FormData();
      imagens.forEach(a => dados.append('files', a));
      const resp = await fetch(`${BASE_URL}/Producoes/${producaoId}/fotos-entrega`, { method: 'POST', body: dados });
      if (!resp.ok) {
        let msg = '';
        try { msg = (await resp.json())?.mensagem; } catch { /* texto simples */ }
        throw new Error(msg || `erro ${resp.status}`);
      }
      await carregar();
    } catch (err) {
      alert('Erro ao enviar as fotos: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  const excluir = async (foto) => {
    if (!window.confirm('Excluir esta foto da entrega?')) return;
    try {
      const resp = await fetch(`${BASE_URL}/Producoes/fotos-entrega/${foto.id}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error(`erro ${resp.status}`);
      if (urlsRef.current[foto.id]) URL.revokeObjectURL(urlsRef.current[foto.id]);
      delete urlsRef.current[foto.id];
      setUrls({ ...urlsRef.current });
      setFotos(prev => prev.filter(f => f.id !== foto.id));
    } catch (err) {
      alert('Erro ao excluir a foto: ' + err.message);
    }
  };

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 860, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>📷 Fotos da entrega · Projeto #{producaoId}</h3>
          <button type="button" onClick={onFechar} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 34, height: 34, padding: 0, fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 6, background: 'rgb(235, 133, 0)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: enviando ? 'wait' : 'pointer', marginBottom: 14 }}>
          {enviando ? 'Enviando...' : '+ Adicionar fotos'}
          <input type="file" accept="image/*" multiple disabled={enviando} style={{ display: 'none' }}
            onChange={(e) => { const arquivos = e.target.files; enviar(arquivos); e.target.value = ''; }} />
        </label>

        {carregando && fotos.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#666' }}>Carregando fotos...</div>
        ) : fotos.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#666', background: '#fafafa', borderRadius: 8 }}>
            Nenhuma foto da entrega ainda. Clique em "+ Adicionar fotos".
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
            {fotos.map(f => (
              <div key={f.id} style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', background: '#fafafa', position: 'relative' }}>
                <div
                  onClick={() => urls[f.id] && window.open(urls[f.id], '_blank')}
                  title="Clique para ver a foto inteira"
                  style={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: urls[f.id] ? 'zoom-in' : 'default', background: '#fff3e0' }}
                >
                  {urls[f.id]
                    ? <img src={urls[f.id]} alt={f.nomeArquivo || 'Foto da entrega'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: 28, opacity: 0.5 }}>📷</span>}
                </div>
                <div style={{ padding: '6px 8px', fontSize: 11, color: '#666' }}>{formatarDataHora(f.dataHora)}</div>
                <button type="button" onClick={() => excluir(f)} title="Excluir foto"
                  style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, padding: 0, borderRadius: '50%', border: '2px solid #fff', background: '#f44336', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
